import type { Model } from '../core/model';
import type { ModelJSON } from '../core/model';
import type { Unit } from '../core/units';
import type { Vec3 } from '../core/vec';
import type { VanState } from '../vans/vans';
import { applyIdBand, applyOpsToModel, applyOpsToJson, diffModels, emptyOps, opsIsEmpty } from './sync';
import { CollabClient, type CollabStatus } from './collabClient';
import { peerViewEqual } from './follow';
import type { EntityOps, MetaOps, PeerInfo, PeerView, RoomDoc, ServerMessage } from '../../server/protocol';

/** Mindestabstand zwischen zwei Ops-Flushes (ms) — Drags werden dadurch live sichtbar. */
const FLUSH_MS = 120;
/** Mindestabstand zwischen zwei Presence-Cursor-Nachrichten (ms). */
const CURSOR_MS = 100;
/** Mindestabstand zwischen zwei Kamera-Ansicht-Nachrichten (ms). */
const VIEW_MS = 100;

/** Schnittstelle, über die der Manager die App erreicht. */
export interface CollabHooks {
  getModel(): Model;
  getVan(): VanState;
  getUnits(): Unit;
  /** Vollen Raum-Stand übernehmen (erster Join oder Reset nach Server-Neustart). */
  loadDoc(doc: RoomDoc): void;
  /** Remote-Metadaten (Wohnmobil/Einheit) übernehmen, ohne die Kamera zu bewegen. */
  applyMeta(meta: MetaOps): void;
  /** Modell wurde remote geändert → neu zeichnen. */
  onChanged(): void;
  onStatus(status: CollabStatus, message?: string): void;
  onPeers(peers: PeerInfo[], you: PeerInfo | null): void;
  /** Ein anderer Teilnehmer lädt uns ein, seiner Ansicht zu folgen. */
  onFollowInvite(msg: Extract<ServerMessage, { t: 'follow-invite' }>): void;
  /** Ein Eingeladener hat unsere Einladung angenommen/abgelehnt. */
  onFollowInviteResponse(msg: Extract<ServerMessage, { t: 'follow-invite-response' }>): void;
}

export type Cursor = [number, number, number] | null;

/** Schlüssel für den gespeicherten Anzeigenamen. */
export const USER_NAME_KEY = 'campsketch.user.name';
/** Gültige Raum-IDs (müssen zur Server-Prüfung passen). */
export const ROOM_ID_RE = /^[A-Za-z0-9_-]{4,64}$/;

/** Kurzven, gut tipbare Raum-ID (kein „l/1/0/O“) — Rate-Sicherheit reicht für Einladungslinks. */
export function newRoomId(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(10));
  let id = '';
  for (const b of bytes) id += chars[b % chars.length];
  return id;
}

/**
 * Kollaborations-Fassade: hält den Shadow-Stand (`base`), den der Server kennt,
 * leitet lokale Änderungen als Entity-Ops weiter, wendet Remote-Ops in place an
 * und verteilt Presence. Konfliktstrategie: Last-Writer-Wins pro Entity.
 */
export class CollabManager {
  readonly roomId: string;
  readonly shareUrl: string;
  private client: CollabClient;
  /** Modellstand, wie der Server ihn kennt (null = noch nicht beigetreten). */
  private base: ModelJSON | null = null;
  private baseMeta: MetaOps = {};
  private slot = 0;
  private youPeer: PeerInfo | null = null;
  private peerList: PeerInfo[] = [];
  private cursorsByPeer = new Map<string, Cursor>();
  private viewsByPeer = new Map<string, PeerView | null>();
  private dirtyModel = false;
  private lastFlushAt = 0;
  private lastCursorSentAt = 0;
  private lastCursorSent: Cursor = null;
  private lastViewSent: PeerView | null = null;
  private lastViewSentAt = 0;
  private firstJoin = true;

  constructor(private hooks: CollabHooks, roomId: string, private uploadDoc?: RoomDoc) {
    this.roomId = roomId;
    // Gäste-Link führt immer auf /join — dort kann man ohne Konto beitreten,
    // egal von welcher Seite (Dashboard, /p/:id) die Session gestartet wurde.
    this.shareUrl = `${location.origin}/join?room=${encodeURIComponent(roomId)}`;
    this.client = new CollabClient({
      onWelcome: (msg) => this.handleWelcome(msg),
      onPeers: (msg) => {
        this.peerList = msg.peers;
        this.prunePresence();
        this.hooks.onPeers(this.peerList, this.youPeer);
      },
      onRemote: (msg) => this.handleRemote(msg),
      onReset: (msg) => this.handleReset(msg.doc),
      onPresence: (msg) => {
        this.cursorsByPeer.set(msg.from.clientId, msg.cursor);
        this.hooks.onPeers(this.peerList, this.youPeer);
      },
      onView: (msg) => {
        this.viewsByPeer.set(msg.from.clientId, msg.view);
      },
      onFollowInvite: (msg) => this.hooks.onFollowInvite(msg),
      onFollowInviteResponse: (msg) => this.hooks.onFollowInviteResponse(msg),
      onStatus: (status, message) => this.hooks.onStatus(status, message)
    });
  }

  // ---------- Lebenszyklus ----------

  connect(name: string): void {
    this.client.connect(this.roomId, name, this.uploadDoc);
  }

  leave(): void {
    this.client.disconnect();
    this.youPeer = null;
    this.peerList = [];
    this.cursorsByPeer.clear();
    this.viewsByPeer.clear();
    this.lastViewSent = null;
  }

  get status(): CollabStatus {
    return this.client.status;
  }

  get connected(): boolean {
    return this.client.connected;
  }

  get you(): PeerInfo | null {
    return this.youPeer;
  }

  get peers(): PeerInfo[] {
    return this.peerList;
  }

  get cursors(): Map<string, Cursor> {
    return this.cursorsByPeer;
  }

  /** Letzte bekannte Kamera-Ansicht eines Teilnehmers (null = keine empfangen). */
  peerView(clientId: string): PeerView | null {
    return this.viewsByPeer.get(clientId) ?? null;
  }

  // ---------- Lokale Änderungen ----------

  /** Vom App-Renderpfad aufrufen: das Modell kann sich geändert haben. */
  markDirty(): void {
    this.dirtyModel = true;
  }

  /**
   * Vom Tick aufrufen: diffed das Modell gegen den Server-Stand und sendet
   * geänderte Entities. Nichts zu tun ist der Normalfall und kostet fast nichts.
   */
  maybeSync(): void {
    if (!this.client.connected || !this.base) return;
    const meta = this.metaDiff();
    if (!this.dirtyModel && !meta) return;
    const now = performance.now();
    if (now - this.lastFlushAt < FLUSH_MS) return;
    this.lastFlushAt = now;

    let ops: EntityOps = emptyOps();
    if (this.dirtyModel) {
      this.dirtyModel = false;
      const model = this.hooks.getModel();
      const cur = model.toJSON();
      ops = diffModels(this.base, cur);
      this.base = cur;
    }
    if (meta) this.baseMeta = { van: this.hooks.getVan(), units: this.hooks.getUnits() };
    if (!opsIsEmpty(ops) || meta) this.client.sendOps(ops, meta ?? undefined);
  }

  /** Cursor-Weltposition an die anderen senden (gedrosselt, null = Cursor weg). */
  sendCursor(cursor: Vec3 | null): void {
    const now = performance.now();
    if (now - this.lastCursorSentAt < CURSOR_MS) return;
    const next: Cursor = cursor ? [cursor.x, cursor.y, cursor.z] : null;
    if (next === null && this.lastCursorSent === null) return;
    this.lastCursorSentAt = now;
    this.lastCursorSent = next;
    this.client.sendPresence(next);
  }

  /**
   * Eigene Kamera-Ansicht an die anderen melden (gedrosselt, nur bei echter Änderung).
   * Nach einem Verbindungsabbruch wird beim nächsten Aufruf der volle Zustand erneut
   * gesendet, damit jemand, der uns gerade folgen will, nicht im Leeren hängt.
   */
  syncView(view: PeerView): void {
    if (!this.client.connected) {
      this.lastViewSent = null;
      return;
    }
    if (peerViewEqual(view, this.lastViewSent)) return;
    const now = performance.now();
    if (now - this.lastViewSentAt < VIEW_MS) return;
    this.lastViewSentAt = now;
    this.lastViewSent = view;
    this.client.sendView(view);
  }

  rename(name: string): void {
    this.client.rename(name);
    // Optimistisch übernehmen — der Server schickt das Peers-Update nur an die
    // anderen, sonst zeigte der eigene Chip bis zum nächsten Event den alten Namen.
    if (this.youPeer) {
      this.youPeer.name = name;
      this.hooks.onPeers(this.peerList, this.youPeer);
    }
  }

  /** Einen Teilnehmer einladen, der eigenen Ansicht zu folgen. */
  inviteFollow(clientId: string): void {
    this.client.sendFollowInvite(clientId);
  }

  /** Eine erhaltene Folge-Einladung beantworten. */
  respondFollowInvite(clientId: string, accepted: boolean): void {
    this.client.sendFollowInviteResponse(clientId, accepted);
  }

  // ---------- Server-Nachrichten ----------

  private handleWelcome(msg: Extract<ServerMessage, { t: 'welcome' }>): void {
    this.youPeer = msg.you;
    this.peerList = msg.peers;
    this.slot = msg.you.slot;
    this.cursorsByPeer.clear();
    this.viewsByPeer.clear();
    // Kamerastände der anderen übernehmen — Folgen greift damit sofort,
    // ohne dass sich der Presenter erst bewegen muss.
    for (const snap of msg.views ?? []) {
      this.viewsByPeer.set(snap.from.clientId, snap.view);
    }
    const hooks = this.hooks;

    if (this.firstJoin) {
      this.firstJoin = false;
      const model = hooks.getModel();
      const localHasContent = model.vertices.size > 0;

      if (this.uploadDoc) {
        // Host: hat seinen Stand gerade hochgeladen — er bleibt lokal erhalten.
        this.base = msg.doc ? msg.doc.model : model.toJSON();
        this.baseMeta = msg.doc
          ? { van: msg.doc.van, units: msg.doc.units }
          : { van: hooks.getVan(), units: hooks.getUnits() };
      } else if (msg.fresh && localHasContent) {
        // Raum ohne jeden Stand (z. B. nach Server-Neustart ohne Persistenz), aber
        // lokal existiert Inhalt → diesen etablieren, statt ihn mit leer zu löschen.
        const doc: RoomDoc = { model: model.toJSON(), van: hooks.getVan(), units: hooks.getUnits() };
        this.client.sendState(doc);
        this.base = doc.model;
        this.baseMeta = { van: doc.van, units: doc.units };
      } else if (msg.doc) {
        // Raum-Stand übernehmen (auch bewusst leer: der Host hat ein leeres Modell geteilt).
        // Achtung: loadDoc tauscht das Model-Objekt — danach immer wieder hooks.getModel() holen.
        hooks.loadDoc(msg.doc);
        this.base = msg.doc.model;
        this.baseMeta = { van: msg.doc.van, units: msg.doc.units };
      } else {
        this.base = model.toJSON();
        this.baseMeta = { van: hooks.getVan(), units: hooks.getUnits() };
      }
      applyIdBand(hooks.getModel(), this.slot);
    } else {
      // Reconnect: Remote-Änderungen aus der Offline-Zeit in das lokale Modell mergen;
      // eigene lokale Änderungen bleiben und werden beim nächsten Flush gesendet.
      if (msg.doc) {
        if (this.base) {
          const ops = diffModels(this.base, msg.doc.model);
          if (!opsIsEmpty(ops)) {
            applyOpsToModel(hooks.getModel(), ops);
            hooks.onChanged();
          }
          if (JSON.stringify({ van: msg.doc.van, units: msg.doc.units }) !== JSON.stringify(this.baseMeta)) {
            hooks.applyMeta({ van: msg.doc.van, units: msg.doc.units });
          }
        }
        this.base = msg.doc.model;
        this.baseMeta = { van: msg.doc.van, units: msg.doc.units };
      }
      applyIdBand(hooks.getModel(), this.slot); // evtl. neuer Slot nach Reconnect
    }
    hooks.onPeers(this.peerList, this.youPeer);
  }

  private handleRemote(msg: Extract<ServerMessage, { t: 'remote' }>): void {
    const model = this.hooks.getModel();
    if (this.base) applyOpsToJson(this.base, msg.ops);
    applyOpsToModel(model, msg.ops);
    if (msg.meta) {
      this.baseMeta = {
        van: msg.meta.van ?? this.baseMeta.van,
        units: msg.meta.units ?? this.baseMeta.units
      };
      this.hooks.applyMeta(msg.meta);
    }
    this.hooks.onChanged();
  }

  private handleReset(doc: RoomDoc): void {
    this.hooks.loadDoc(doc);
    this.base = doc.model;
    this.baseMeta = { van: doc.van, units: doc.units };
    applyIdBand(this.hooks.getModel(), this.slot);
  }

  private metaDiff(): MetaOps | null {
    const van = this.hooks.getVan();
    const units = this.hooks.getUnits();
    if (JSON.stringify({ van, units }) === JSON.stringify(this.baseMeta)) return null;
    return { van, units };
  }

  private prunePresence(): void {
    const alive = new Set(this.peerList.map((p) => p.clientId));
    for (const id of [...this.cursorsByPeer.keys()]) {
      if (!alive.has(id)) this.cursorsByPeer.delete(id);
    }
    for (const id of [...this.viewsByPeer.keys()]) {
      if (!alive.has(id)) this.viewsByPeer.delete(id);
    }
  }
}
